import React, { useState } from "react";
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import dayjs from "dayjs";
import toast, { Toaster } from "react-hot-toast";
import { BuyBlockModal } from "../components/BuyBlockModal";
import { CapacityBlockCard } from "../components/CapacityBlockCard";
import { PlanChangeModal } from "../components/PlanChangeModal";
import { PlanLimitBar } from "../components/PlanLimitBar";
import { UpgradeNudge } from "../components/UpgradeNudge";
import {
  useCapacityBlocks,
  useChangePlan,
  usePlanChangePreview,
  useSaasInvoices,
  useSaasPlans,
  useSaasSubscription,
} from "../hooks";

const STATUS_LABELS = {
  TRIAL: { label: "Prueba gratuita", variant: "info" },
  ACTIVE: { label: "Activo", variant: "success" },
  PAST_DUE: { label: "Pago vencido", variant: "danger" },
  CANCELED: { label: "Cancelado", variant: "secondary" },
};

function formatCOP(amount) {
  if (amount == null) return "—";
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
  }).format(amount);
}

// ── Tab 1: Mi plan ─────────────────────────────────────────────────────────────

// Feature rows shown inside every pricing card, in order.
function planFeatures(p) {
  const limit = (v, sing, plur) => (v == null ? `${plur} ilimitados` : `${v} ${v === 1 ? sing : plur}`);
  return [
    { ok: true, text: limit(p.max_active_students, "estudiante activo", "estudiantes activos") },
    { ok: true, text: limit(p.max_headquarters, "sede", "sedes") },
    { ok: true, text: limit(p.max_teachers, "docente", "docentes") },
    { ok: !!p.features?.has_honorarios, text: "Honorarios docentes" },
    { ok: !!p.features?.has_multi_sede, text: "Multi-sede consolidado" },
  ];
}

// Single pricing card — highlighted when it's the tenant's current plan.
function PlanCard({ p, isCurrent, currentPrice, onChangePlan, changePlanLoading }) {
  const price = Number(p.monthly_price_cop) || 0;
  const isUpgrade = price > currentPrice;

  return (
    <Card
      className={`h-100 text-center position-relative ${
        isCurrent ? "border-success shadow" : "border-0 shadow-sm"
      }`}
      style={isCurrent ? { borderWidth: 2 } : undefined}
    >
      {isCurrent && (
        <Badge
          bg="success"
          className="position-absolute top-0 start-50 translate-middle"
          style={{ letterSpacing: ".02em" }}
        >
          Plan actual
        </Badge>
      )}
      <Card.Body className="p-4 d-flex flex-column">
        <h5 className={`mb-1 ${isCurrent ? "text-success" : ""}`}>{p.name}</h5>
        <div className="my-2">
          <span className="fw-bold" style={{ fontSize: "1.9rem", lineHeight: 1 }}>
            {price === 0 ? "Gratis" : formatCOP(price)}
          </span>
          {price > 0 && <div className="text-muted small">COP / mes + IVA</div>}
        </div>
        {price > 0 && !isCurrent && (
          <div className="mb-1">
            <span
              className="badge rounded-pill"
              style={{ background: "rgba(31,157,107,0.16)", color: "#155c43", fontWeight: 600 }}
            >
              <i className="mdi mdi-gift-outline me-1" aria-hidden="true" />
              21 días de prueba gratis
            </span>
          </div>
        )}
        <ul className="list-unstyled text-start small flex-grow-1 mt-2 mb-3">
          {planFeatures(p).map((f) => (
            <li key={f.text} className={`mb-2 d-flex align-items-start gap-2 ${f.ok ? "" : "text-muted"}`}>
              <i
                className={`mdi ${f.ok ? "mdi-check-circle text-success" : "mdi-close-circle text-muted"}`}
                aria-hidden="true"
                style={{ marginTop: 1 }}
              />
              <span>{f.text}</span>
            </li>
          ))}
        </ul>
        {isCurrent ? (
          <Button variant="outline-success" disabled aria-label={`${p.name} es tu plan actual`}>
            Tu plan
          </Button>
        ) : (
          <Button
            variant={isUpgrade ? "primary" : "outline-secondary"}
            disabled={changePlanLoading}
            onClick={() => onChangePlan(p)}
            aria-label={`Cambiar al plan ${p.name}`}
          >
            {isUpgrade ? "Mejorar" : "Cambiar"} a {p.name}
          </Button>
        )}
      </Card.Body>
    </Card>
  );
}

function MiPlanTab({ subscription, plans, onChangePlan, changePlanLoading }) {
  const {
    plan,
    status,
    trial_ends_at,
    effective_limits,
    current_usage,
    pending_plan,
    scheduled_plan,
    suggest_upgrade,
    upgrade_to,
    message,
  } = subscription ?? {};
  const statusInfo = STATUS_LABELS[status] ?? { label: status, variant: "secondary" };
  const currentPrice = Number(plan?.monthly_price_cop) || 0;

  return (
    <>
      {suggest_upgrade && (
        <UpgradeNudge
          suggest_upgrade={suggest_upgrade}
          upgrade_to={upgrade_to}
          message={message}
          onDismiss={undefined}
        />
      )}

      {/* Plan actual */}
      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex align-items-center gap-2 flex-wrap mb-3">
            <h5 className="mb-0">{plan?.name ?? "—"}</h5>
            <Badge bg={statusInfo.variant}>{statusInfo.label}</Badge>
            {status === "TRIAL" && trial_ends_at && (
              <span className="small text-muted">
                Prueba hasta {dayjs(trial_ends_at).format("DD/MM/YYYY")}
              </span>
            )}
          </div>

          {pending_plan && (
            <Alert variant="info" className="py-2 small mb-3">
              <i className="mdi mdi-clock-outline me-1" aria-hidden="true" />
              Cambio a <strong>{pending_plan.name}</strong> pendiente de pago. Se
              activará cuando confirmemos tu pago.
            </Alert>
          )}
          {scheduled_plan && (
            <Alert variant="warning" className="py-2 small mb-3">
              <i className="mdi mdi-calendar-clock me-1" aria-hidden="true" />
              Bajarás al plan <strong>{scheduled_plan.name}</strong> al terminar tu
              período actual.
            </Alert>
          )}

          <PlanLimitBar
            label="Sedes"
            used={current_usage?.headquarters ?? 0}
            plan_limit={plan?.max_headquarters}
            effective_limit={effective_limits?.headquarters}
          />
          <PlanLimitBar
            label="Estudiantes activos"
            used={current_usage?.students ?? 0}
            plan_limit={plan?.max_active_students}
            effective_limit={effective_limits?.students}
          />
          <PlanLimitBar
            label="Docentes"
            used={current_usage?.teachers ?? 0}
            plan_limit={plan?.max_teachers}
            effective_limit={effective_limits?.teachers}
          />
        </Card.Body>
      </Card>

      {/* Comparativa de planes — tarjetas de precios */}
      {plans && plans.length > 0 && (
        <>
          <h6 className="mb-1">Comparar planes</h6>
          <p className="text-muted small mb-3">
            Cambia de plan cuando tu academia crezca. Anual: paga 10 meses y recibe 12.
          </p>
          <Row className="g-3">
            {plans.map((p) => (
              <Col xs={12} sm={6} lg={3} key={p.id}>
                <PlanCard
                  p={p}
                  isCurrent={p.name === plan?.name}
                  currentPrice={currentPrice}
                  onChangePlan={onChangePlan}
                  changePlanLoading={changePlanLoading}
                />
              </Col>
            ))}
          </Row>
        </>
      )}
    </>
  );
}

// ── Tab 2: Bloques de capacidad ────────────────────────────────────────────────

// Catálogo de bloques disponibles para contratar (mismo contrato que el backend).
const BLOCK_CATALOG = [
  {
    kind: "STUDENTS",
    icon: "mdi-account-multiple-plus",
    title: "+50 estudiantes activos",
    desc: "Amplía cuántos estudiantes puedes tener activos al tiempo.",
    price: 25000,
  },
  {
    kind: "TEACHERS",
    icon: "mdi-account-tie",
    title: "+5 docentes",
    desc: "Suma cupos de docentes a tu plan actual.",
    price: 20000,
  },
  {
    kind: "HEADQUARTERS",
    icon: "mdi-map-marker-plus",
    title: "+1 sede",
    desc: "Administra una sede adicional con agenda y reportes propios.",
    price: 49000,
  },
];

function BloquesTab({ subscription }) {
  const { data: blocks, isLoading } = useCapacityBlocks();
  const [buyKind, setBuyKind] = useState(null); // null = modal cerrado
  const { suggest_upgrade, upgrade_to, message } = subscription ?? {};

  return (
    <>
      {suggest_upgrade && (
        <UpgradeNudge
          suggest_upgrade={suggest_upgrade}
          upgrade_to={upgrade_to}
          message={message}
          onDismiss={undefined}
        />
      )}

      {/* Bloques disponibles (catálogo) */}
      <h6 className="mb-1">Bloques disponibles</h6>
      <p className="text-muted small mb-3">
        Amplían tus límites sin cambiar de plan. Se cobran mensualmente y puedes
        cancelarlos cuando quieras.
      </p>
      <Row className="g-3 mb-4">
        {BLOCK_CATALOG.map((b) => (
          <Col xs={12} md={4} key={b.kind}>
            <Card className="h-100 border-0 shadow-sm text-center">
              <Card.Body className="p-4 d-flex flex-column">
                <div className="mb-2" style={{ fontSize: "2rem", lineHeight: 1 }}>
                  <i className={`mdi ${b.icon} text-primary`} aria-hidden="true" />
                </div>
                <h6 className="mb-1">{b.title}</h6>
                <p className="text-muted small flex-grow-1 mb-2">{b.desc}</p>
                <div className="fw-bold mb-3">
                  {formatCOP(b.price)}
                  <span className="text-muted fw-normal small"> / mes</span>
                </div>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setBuyKind(b.kind)}
                  aria-label={`Comprar bloque ${b.title}`}
                >
                  Comprar
                </Button>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Bloques contratados */}
      <h6 className="mb-3">Mis bloques contratados</h6>

      {isLoading && <Spinner size="sm" />}

      {!isLoading && blocks?.length === 0 && (
        <Alert variant="secondary" className="small mb-0">
          Aún no has contratado bloques adicionales. Elige uno del catálogo de arriba
          cuando necesites más capacidad.
        </Alert>
      )}

      {(blocks ?? []).map((block) => (
        <CapacityBlockCard key={block.id} block={block} />
      ))}

      <BuyBlockModal
        show={buyKind != null}
        initialKind={buyKind ?? "STUDENTS"}
        onHide={() => setBuyKind(null)}
      />
    </>
  );
}

// ── Tab 3: Facturas ────────────────────────────────────────────────────────────

function FacturasTab() {
  const { data: invoices, isLoading, isError } = useSaasInvoices();

  if (isLoading) return <Spinner size="sm" />;

  if (isError) {
    return (
      <Alert variant="secondary" className="small">
        Historial de facturas no disponible en este momento.{" "}
        {/* Endpoint /api/v1/saas/invoices/ pendiente de TASK-17 */}
        Consulta con el administrador del sistema.
      </Alert>
    );
  }

  if (!invoices || invoices.length === 0) {
    return <Alert variant="secondary" className="small">No hay facturas registradas.</Alert>;
  }

  return (
    <div className="table-responsive">
      <Table hover bordered size="sm" className="align-middle">
        <thead className="table-light">
          <tr>
            <th>Periodo</th>
            <th>Monto</th>
            <th>IVA (19%)</th>
            <th>Total</th>
            <th>Estado</th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((inv) => (
            <tr key={inv.id}>
              <td>{inv.period ?? inv.id}</td>
              <td>{formatCOP(inv.amount)}</td>
              <td>{formatCOP(inv.tax_amount)}</td>
              <td>{formatCOP((inv.amount ?? 0) + (inv.tax_amount ?? 0))}</td>
              <td>
                <Badge bg={inv.status === "PAID" ? "success" : "warning"}>
                  {inv.status}
                </Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

// ── PlanPage ───────────────────────────────────────────────────────────────────

const TABS = [
  { key: "mi-plan", title: "Mi plan" },
  { key: "bloques", title: "Bloques" },
  { key: "facturas", title: "Facturas" },
];

function PlanPage() {
  const [activeTab, setActiveTab] = useState("mi-plan");
  const { data: subscription, isLoading: subLoading, isError: subError } = useSaasSubscription();
  const { data: plans, isLoading: plansLoading } = useSaasPlans();
  const changePlan = useChangePlan();
  const preview = usePlanChangePreview();
  // { plan, preview } mientras se confirma un cambio; null = modal cerrado.
  const [pendingChange, setPendingChange] = useState(null);

  // Paso 1: el dueño elige un plan → consultamos el preview para saber si el
  // cambio requiere el wizard (downgrade con exceso) antes de confirmar.
  function handleRequestChange(plan) {
    preview.mutate(plan.id, {
      onSuccess: (pv) => {
        if (pv.result === "noop") {
          toast("Ya tienes este plan.");
          return;
        }
        setPendingChange({ plan, preview: pv });
      },
      onError: () => toast.error("No se pudo preparar el cambio de plan."),
    });
  }

  // Paso 2: confirmar (con la selección del wizard si aplica).
  function handleConfirmChange(keep) {
    if (!pendingChange) return;
    changePlan.mutate(
      { planId: pendingChange.plan.id, keep },
      {
        onSuccess: (data) => {
          toast.success(data?.change_result?.message ?? "Cambio de plan aplicado.");
          setPendingChange(null);
        },
        onError: (err) =>
          toast.error(
            err?.response?.data?.detail ?? "No se pudo cambiar de plan."
          ),
      }
    );
  }

  if (subLoading || plansLoading) {
    return (
      <Container className="py-4 text-center">
        <Spinner role="status" aria-label="Cargando plan" />
      </Container>
    );
  }

  if (subError) {
    return (
      <Container className="py-4">
        <Alert variant="danger">No se pudo cargar la información del plan.</Alert>
      </Container>
    );
  }

  const tabContent = {
    "mi-plan": (
      <MiPlanTab
        subscription={subscription}
        plans={plans}
        onChangePlan={handleRequestChange}
        changePlanLoading={preview.isPending || changePlan.isPending}
      />
    ),
    bloques: <BloquesTab subscription={subscription} />,
    facturas: <FacturasTab />,
  };

  return (
    <Container className="py-4" style={{ maxWidth: "900px" }}>
      <Toaster position="top-right" />
      <h4 className="mb-4">Plan y facturación</h4>

      {/* ── Desktop: Tabs (d-none d-sm-flex) ── */}
      <div className="d-none d-sm-block">
        <ul className="nav nav-tabs mb-3" role="tablist">
          {TABS.map((t) => (
            <li key={t.key} className="nav-item" role="presentation">
              <button
                className={`nav-link${activeTab === t.key ? " active" : ""}`}
                role="tab"
                aria-selected={activeTab === t.key}
                aria-controls={`panel-${t.key}`}
                id={`tab-${t.key}`}
                onClick={() => setActiveTab(t.key)}
              >
                {t.title}
              </button>
            </li>
          ))}
        </ul>
        <div
          id={`panel-${activeTab}`}
          role="tabpanel"
          aria-labelledby={`tab-${activeTab}`}
        >
          {tabContent[activeTab]}
        </div>
      </div>

      {/* ── Mobile: Accordion (d-sm-none) ── */}
      <Accordion className="d-sm-none" defaultActiveKey="mi-plan">
        {TABS.map((t) => (
          <Accordion.Item key={t.key} eventKey={t.key}>
            <Accordion.Header>{t.title}</Accordion.Header>
            <Accordion.Body>{tabContent[t.key]}</Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>

      <PlanChangeModal
        show={pendingChange != null}
        plan={pendingChange?.plan}
        preview={pendingChange?.preview}
        loading={changePlan.isPending}
        onHide={() => setPendingChange(null)}
        onConfirm={handleConfirmChange}
      />
    </Container>
  );
}

export { PlanPage };
